import {Component, HostListener, OnInit} from '@angular/core';
import { CepRule, SimpleCepRule } from "../../model/cep-rule";
import { SortOrder } from "../../model/sort-order";
import { CepRuleFilters } from "../../model/filters/cep-rule-filters";
import { CepRulesService } from "../../services/cep-rules.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { CommonMessagesService } from "../../services/common-messages.service";
import { TenantsService } from "../../services/tenants.service";
import { AuthGuard } from "../../common/auth-guard.service";
import { Tenant } from "../../model/tenant";
import {FiltersService} from "../../services/filters.service";

@Component({
  selector: 'app-cep-rules',
  templateUrl: './cep-rules.component.html',
  styleUrls: ['./cep-rules.component.scss']
})
export class CepRulesComponent implements OnInit {

  public rules: CepRule[];
  public totalRules: number;
  public loadingRules: boolean;
  public tenants: Tenant[];
  public ruleStates: string[];

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: CepRuleFilters;

  public activeButtonState: boolean;
  public filtersFilled: boolean;

  constructor(private cepRulesService: CepRulesService,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              private confirmationService: ConfirmationService,
              public authGuardService: AuthGuard,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<CepRuleFilters>) {
    this.ruleStates = ['ACTIVE', 'PAUSE', 'DELETE', 'CONTROL'];
    this.currentFilters = new CepRuleFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    if(this.authGuardService.superAdminRequired()) {
      this.tenantsService.getTenants().subscribe(response => {
        this.tenants = response.tenants;
        Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      });
    } else {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    }
    this.rules = new Array(this.rows);
  }

  private loadRules(offset: number, limit: number): void {
    this.loadingRules = true;
    this.rules = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.cepRulesService.getCepRules(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters: this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.rules = response.cepRules;
        this.totalRules = response.totalElements;
        // this.loadEvaluations();
        this.loadingRules = false;
      });
  }

  // private loadEvaluations() {
  //   this.rules.forEach((cepRule: CepRule)=> {
  //     let fired = true;
  //     cepRule.simpleRules.forEach((simpleRule: SimpleCepRule)=> {
  //       if(simpleRule.evaluationDate != undefined) {
  //         if(cepRule.evaluationDate == undefined || simpleRule.evaluationDate > cepRule.evaluationDate) {
  //           cepRule.evaluationDate = simpleRule.evaluationDate;
  //           cepRule.triggeredDate = simpleRule.triggeredDate;
  //           cepRule.lastValueEvaluated = simpleRule.lastValueEvaluated;
  //           cepRule.lastSensorEvaluated = simpleRule.entitiesAndSensorIdentifiers[0].measurementName;
  //         }
  //         if(simpleRule.lastEvaluation == false) {
  //           fired = false;
  //         }
  //       } else {
  //         fired = false;
  //       }
  //       cepRule.lastEvaluation = fired;
  //     });
  //   });
  // }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadRules(event.first/event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new CepRuleFilters();
    this.currentFilters.sortField = 'nameComplexRule';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true){
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadRules(this.first/this.rows, this.rows);
  }

  confirmAndDelete(cepRule: CepRule): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      let organizationUuid = cepRule.organization ? cepRule.organization.uuid : null;
      this.deleteCepRule(cepRule.tenant.uuid, organizationUuid, cepRule.nameComplexRule);
    });
  }

  deleteCepRule(tenantUuid: string, organizationUuid: string, nameComplexRule: string): void {
    this.tenantsService.deleteTenantCepRule(tenantUuid, organizationUuid, nameComplexRule).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadRules(this.first/this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    })
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if(this.selected.length == 0) return;
    let identifiers = this.selected.map(item => ({
      tenantUuid: item.tenant.uuid,
      organizationUuid: item.organization ? item.organization.uuid : null,
      name: item.nameComplexRule}));

    this.cepRulesService.deleteCepRules(identifiers).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadRules(this.first/this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  trimFieldsForm(filters: CepRuleFilters): void {
    Object.keys(filters).forEach((key)=> {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  evaluateActiveButtonState(): void {
    let state;
    this.selected.forEach(event => {
      if (event.ruleState === 'ACTIVE') {
        state = true;
      } else if (event.ruleState === 'PAUSE') {
        if (state === undefined) {
          state = false;
        }
      }
    });
    this.activeButtonState = !state;
  }

  changeActiveStates(): void {
    this.cepRulesService.updateRuleState(this.selected, !this.activeButtonState ? 'ACTIVE' : 'PAUSE').toPromise().then(() => {
      const summary = this.i18n.translate('success.generic.changesApplied');
      const detail = this.i18n.translate('success.triggers.changedState');
      this.messageService.add({severity: 'success', summary, detail});
      this.loadRules(this.first / this.rows, this.rows);
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
